import type {
    ResourceTypeHandle,
    Tool,
} from '@toolproof-core/genesis/_contract/_index';

import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';
import { extractResourceTypeHandleFromRole } from '@toolproof-core/lib/_pure/workflow-extraction';
import type { MutableWorkflowState } from '@toolproof-core/lib/_contract/workflow-overlay';
import type {
    MutableBranchStepKey,
    MutableLoopStepKey,
    MutableToolStepKey,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import {
    withMergedEntityItemParametricState,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type {
    EntityItem,
} from '@toolproof-core/visualization/_contract/item';
import type { CollectionKey, ConstellationKey } from '@toolproof-core/visualization/_contract/key';
import type {
    ConnectorItemCollectionDerivation,
    ConnectorItemDerivation,
    EntityConstellationDerivation,
    EntityItemCollectionDerivation,
    EntityItemDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    getResourceTypeRingRadius,
    getToolRingOuterRadius,
    getToolStepRingGap,
    isMacroStepConnectorsEnabled,
    isResourceTypeRadialConnectorsEnabled,
    isWorkflowStateEntryConnectorsEnabled,
    resolveCasesConfig,
    resolveMacroStepConnectorsConfig,
    resolveToolStepLayoutConfig,
    resolveWorkflowStateEntriesConfig,
} from '@/workflow/_pure/config/derivationConfig';
import {
    deriveCaseBandGeometry,
} from '@/workflow/_pure/derivation/caseGeometry';
import { deriveCaseConnectorSeeds } from '@/workflow/_pure/derivation/caseMembership';
import {
    buildStepIndexByStepKey,
    isLoopCaseSlot,
    resolveCasePairStepIndexes,
    upsertCasePair,
    type MutableCasePair,
} from '@/workflow/_pure/derivation/caseSeedScaffolding';
import {
    buildCaseItemCollectionDerivations,
    buildMacroStepConnectorItemCollectionDerivations,
    buildResourceTypeRadialConnectorItemCollectionDerivation,
    buildToolStepItemCollectionDerivation,
    buildWorkflowStateEntryConnectorItemCollectionDerivations,
    buildWorkflowStateEntryItemCollectionDerivations,
} from '@/workflow/_pure/derivation/collections';
import {
    appendConnectorItemDerivationToCollectionGroup,
    deriveWorkflowStateEntryConnectorEndpointPair,
    resolveSourceRoleDirectionAndValue,
    tryReadInternalInputPotentialSourceAddress,
} from '@/workflow/_pure/derivation/connectors';
import {
    buildToolStepConstellationDerivation,
    buildWorkflowStateEntryConstellationDerivations,
} from '@/workflow/_pure/derivation/constellations';
import {
    buildItemOffsetsForSpoke,
    deriveAdaptiveRingRadii,
    deriveMaximumAdjacentWorkflowStateEntryArity,
    deriveRadialLayoutMetrics,
    deriveToolStepTubeRadii,
    validateRadialLayoutConfig,
} from '@/workflow/_pure/derivation/geometry';
import {
    toCaseItemDerivation,
    toMacroStepConnectorItemDerivation,
    toResourceTypeRadialConnectorItemDerivation,
    toWorkflowStateEntryConnectorItemDerivation,
    toWorkflowStateEntryItemDerivation,
} from '@/workflow/_pure/derivation/itemFactories';
import { toToolStepEntityItemDerivation } from '@/workflow/_pure/derivation/itemFactories';
import {
    deriveToolStepBounds,
    upsertMacroStepBounds,
} from '@/workflow/_pure/derivation/macroStepBounds';
import { deriveResourceTypeAngularInfo, resolveMacroStepHelperLaneAngles } from '@/workflow/_pure/derivation/resourceTypeAngularPlacement';
import {
    buildMacroStepConnectorCollectionKey,
    parseToolStepCollectionKey,
    parseWorkflowStateEntryCollectionKey,
    parseWorkflowStateEntryItemKey,
    type WorkflowKeyScope,
} from '@/workflow/_pure/keyCodec';
import { parseToolStepKeyFromItemKey } from '@/workflow/_pure/parsing';
import { RADIAL_PANEL_CLEARANCE } from '@/workflow-runner/_data/radialPanelGaps';
import { resolveOperationPresentationMode } from '@/workflow-runner/_pure/config/config';
import { requireConfig } from '@/workflow-runner/_pure/config/config';
import {
    deriveRootLoopStepKeyByCloneLoopStepKey,
    normalizeLoopOwnerStepKey,
} from '@/workflow-runner/_pure/model/traceHelpers';
import { resolvePrimitiveRodConfig } from '@/workflow-runner/_pure/primitiveOperationRod';
import { deriveMaximumOperationRodBandRadius } from '@/workflow-runner/_pure/derivation/operationRodEnvelope';
import {
    resolveWorkflowStateEntryMaterializedEntry,
} from '@/workflow-runner/_pure/derivation/workflowStateEntries';
import type { ToolStepSlotSnapshot } from '@/workflow/_pure/derivation/geometry';
import type { Config as AdapterConfig } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type {
    CaseCollectionsDerivation,
    CaseDerivationInput,
    CaseMetadata,
    CaseSeed,
    MacroStepConnectorDerivationInput,
    MacroStepConnectorSeed,
    ToolStepLayoutInput,
    ToolStepLayoutMetrics,
    WorkflowStateEntryCollectionSeed as SpokeCollectionSeed,
    WorkflowStateEntryConnectorDerivationInput,
    WorkflowStateEntryDerivationInput,
} from '@/workflow/_contract/kernel';
import type { Config } from '@/workflow-runner/_contract/config';
import type {
    TraceSnapshotView,
} from '@/workflow-runner/_contract/traceSnapshot';

function shouldPrunePostWinningBranchCases(args: {
    cosmosData: CosmosDataModel;
    config: AdapterConfig;
}): boolean {
    void args;
    return true;
}


export function deriveToolStepLayoutMetrics(
    args: ToolStepLayoutInput<CosmosDataModel, TraceSnapshotView>
): ToolStepLayoutMetrics<TraceSnapshotView> | undefined {
    const { cosmosData, itemCollections, config } = args;
    const runnerConfig = requireConfig(config);
    const traceSnapshot = args.traceSnapshot;
    if (!traceSnapshot) return undefined;

    const orderedToolSteps = traceSnapshot.position.orderedToolSteps;
    if (orderedToolSteps.length === 0) return undefined;

    const resourceTypeInfo = deriveResourceTypeAngularInfo({ itemCollections, config });
    const resourceTypeRingRadius = getResourceTypeRingRadius(config);
    const toolStepLayout = resolveToolStepLayoutConfig(config);
    validateRadialLayoutConfig({
        toolRingOuterRadius: getToolRingOuterRadius(config),
        resourceTypeRingRadiusMultiplier: config.geometryScale.multipliers.resourceTypes.ringRadiusFromRoot,
        toolStepRingGapMultiplier: config.geometryScale.multipliers.toolSteps.ringGapFromResourceTypeRing,
    });
    const workflowStateEntriesConfig = resolveWorkflowStateEntriesConfig(config);
    const maximumArity = deriveMaximumAdjacentWorkflowStateEntryArity({
        orderedToolSteps,
        toolMap: cosmosData.toolMap,
    });
    const radialLayout = deriveRadialLayoutMetrics({
        resourceTypeRingRadius,
        toolStepRingGap: getToolStepRingGap(config),
        toolStepCount: orderedToolSteps.length,
        maxArityBetweenToolSteps: maximumArity,
    });
    const configuredStartRadius = radialLayout.toolStepRingRadii[0] ?? resourceTypeRingRadius;
    const resourceTypeSphereRadius =
        config.domainPresentation.entities.resourceTypes.item?.shape?.radius ?? 0;
    const operationRodPerpendicularRadius = resolveOperationPresentationMode(runnerConfig) === 'wheels'
        ? deriveMaximumOperationRodBandRadius({
            workflowState: traceSnapshot.position.workflowState,
            tickTracePoints: traceSnapshot.tickTracePoints,
            primitiveConfig: resolvePrimitiveRodConfig(runnerConfig),
        })
        : undefined;
    const ringLayout = deriveAdaptiveRingRadii({
        orderedToolSteps,
        toolMap: cosmosData.toolMap,
        startRadius: configuredStartRadius,
        defaultRingGap: radialLayout.toolStepRingGap,
        workflowStateEntriesConfig,
        resourceTypeInfo,
        ...(operationRodPerpendicularRadius === undefined
            ? {}
            : { perpendicularRadius: operationRodPerpendicularRadius }),
        minimumInnerBoundaryRadius: resourceTypeRingRadius + resourceTypeSphereRadius,
    });
    const { startRadius, ringGap, ringRadii } = ringLayout;
    const tubeRadiusMultipliersByToolStepSlot = toolStepLayout.tubeRadiusMultipliersByToolStepSlot ?? {
        topLevel: 1.0,
        what: 0.85,
        when: 0.7,
    };
    const toolStepSlotSnapshot: ToolStepSlotSnapshot = {
        branchWhatToolStepKeys: traceSnapshot.macroStepControl.branch.authored.branchWhatToolStepKeys,
        branchWhenToolStepKeys: traceSnapshot.macroStepControl.branch.authored.branchWhenToolStepKeys,
        loopWhatToolStepKeys: traceSnapshot.macroStepControl.loop.loopWhatToolStepKeys,
        loopWhenToolStepKeys: traceSnapshot.macroStepControl.loop.loopWhenToolStepKeys,
    };
    const tubeRadii = deriveToolStepTubeRadii({
        orderedToolSteps,
        traceSnapshot: toolStepSlotSnapshot,
        baseTubeRadius: toolStepLayout.tubeRadius,
        tubeRadiusMultipliersByToolStepSlot,
    });

    return {
        traceSnapshot,
        orderedToolStepsBeforeBranchExitPrune: traceSnapshot.position.orderedToolStepsBeforeBranchExitPrune,
        orderedToolSteps,
        startRadius,
        ringGap,
        workflowStateEntryGap: radialLayout.workflowStateEntryGap,
        ringRadii,
        tubeRadii,
    };
}

export function deriveToolStepRadiusByStepKey(args: {
    cosmosData: CosmosDataModel;
    itemCollections: ItemCollection[];
    config: Config;
    traceSnapshot?: TraceSnapshotView;
}): Map<MutableToolStepKey, number> {
    const metrics = deriveToolStepLayoutMetrics({
        cosmosData: args.cosmosData,
        itemCollections: args.itemCollections,
        config: args.config,
        ...(args.traceSnapshot ? { traceSnapshot: args.traceSnapshot } : {}),
    });
    if (!metrics) return new Map();

    const radiusByStepKey = new Map<MutableToolStepKey, number>();
    metrics.orderedToolSteps.forEach((toolStep, index) => {
        const stepKey = toolStep.stepKey as MutableToolStepKey;
        const stepRadius = metrics.ringRadii[index];
        if (typeof stepRadius !== 'number' || !Number.isFinite(stepRadius)) {
            throw new Error(`Missing tool-step radius for ${toolStep.stepKey}`);
        }
        radiusByStepKey.set(stepKey, stepRadius);
    });

    return radiusByStepKey;
}

function collectLoopCasePairsFromTrace(args: {
    traceSnapshot: TraceSnapshotView;
    stepIndexByStepKey: ReadonlyMap<MutableToolStepKey, number>;
}): MutableCasePair[] {
    const { traceSnapshot, stepIndexByStepKey } = args;
    const pairByKey = new Map<string, MutableCasePair>();

    for (const tracePoint of traceSnapshot.tickTracePoints) {
        if (tracePoint.tracePointKind !== 'tick') continue;
        if (tracePoint.tickKind !== CONSTANTS.Enums.TickKind.tool_execution) continue;
        if (tracePoint.stepKind !== CONSTANTS.Enums.StepKind.tool) continue;

        const ownerMacroStepKey = tracePoint.macroStepContext.ownerMacroStepKey;
        const slotInOwner = tracePoint.macroStepContext.slotInOwner;
        if (!ownerMacroStepKey || !slotInOwner) continue;
        if (!isLoopCaseSlot(slotInOwner)) continue;

        const ownerCaseIndex = tracePoint.macroStepContext.ownerCaseIndex ?? 0;
        const pairKey = `${ownerMacroStepKey}|case|${ownerCaseIndex}`;

        const runtimeStepKey = tracePoint.stepKey as MutableToolStepKey;
        const branchStepKey = traceSnapshot.macroStepControl.branch.replay.runtimeToBranchToolStepKey.get(runtimeStepKey)
            ?? runtimeStepKey;

        if (!stepIndexByStepKey.has(branchStepKey as MutableToolStepKey)) continue;

        upsertCasePair({
            pairByKey,
            pairKey,
            ownerMacroStepKey,
            ownerCaseIndex,
            slotFamily: 'case',
            slotInOwner,
            stepKey: branchStepKey as MutableToolStepKey,
        });
    }

    return Array.from(pairByKey.values());
}

export function deriveCaseMembership(args: {
    cosmosData: CosmosDataModel;
    config: Config;
    traceSnapshot?: TraceSnapshotView;
}): CaseMetadata[] {
    const { traceSnapshot } = args;
    if (!traceSnapshot) return [];
    const { orderedToolSteps } = traceSnapshot.position;

    const stepIndexByStepKey = buildStepIndexByStepKey({
        orderedToolSteps,
    });

    const seeds: CaseMetadata[] = [];

    const appendCaseSeedFromPair = (pair: MutableCasePair): void => {
        const stepIndexes = resolveCasePairStepIndexes({ pair, stepIndexByStepKey });
        if (!stepIndexes) return;

        const { whenStepIndex, whatStepIndex } = stepIndexes;
        const whenToolStepKey = pair.whenToolStepKey;
        const whatToolStepKey = pair.whatToolStepKey;
        if (!whenToolStepKey || !whatToolStepKey) return;

        seeds.push({
            ownerMacroStepKey: pair.ownerMacroStepKey,
            ownerCaseIndex: pair.ownerCaseIndex,
            slotFamily: pair.slotFamily,
            whenToolStepKey,
            whatToolStepKey,
            whenStepIndex,
            whatStepIndex,
        });
    };

    const winningBranchCaseIndexByOwner = traceSnapshot.macroStepControl.branch.replay.winningBranchCaseIndexByOwner;
    const shouldPrunePostWinningCases = shouldPrunePostWinningBranchCases({
        cosmosData: args.cosmosData,
        config: args.config,
    });

    // Branch cases are derived from replay-applied branch-step control so skipped
    // cases remain visible before branch_exit pruning.
    const branchPairByKey = new Map<string, MutableCasePair>();
    for (const [addressKey, whenToolStepKeyRaw] of traceSnapshot.macroStepControl.branch.authored.branchToolStepKeyByAddress.entries()) {
        const [ownerMacroStepKey, ownerCaseIndexRaw, slotInOwner] = addressKey.split('|');
        if (!ownerMacroStepKey || !ownerCaseIndexRaw || !slotInOwner) continue;
        if (slotInOwner !== 'cases/when') continue;

        const ownerCaseIndex = Number.parseInt(ownerCaseIndexRaw, 10);
        if (!Number.isFinite(ownerCaseIndex) || ownerCaseIndex < 0) continue;

        const winningCaseIndex = winningBranchCaseIndexByOwner.get(ownerMacroStepKey as MutableBranchStepKey);
        if (shouldPrunePostWinningCases && winningCaseIndex !== undefined && ownerCaseIndex > winningCaseIndex) continue;

        const whatToolStepKeyRaw = traceSnapshot.macroStepControl.branch.authored.branchToolStepKeyByAddress.get(
            `${ownerMacroStepKey}|${ownerCaseIndexRaw}|cases/what`
        );
        if (!whatToolStepKeyRaw) continue;

        const whenToolStepKey = whenToolStepKeyRaw as MutableToolStepKey;
        const whatToolStepKey = whatToolStepKeyRaw as MutableToolStepKey;
        if (!stepIndexByStepKey.has(whenToolStepKey) || !stepIndexByStepKey.has(whatToolStepKey)) continue;

        upsertCasePair({
            pairByKey: branchPairByKey,
            pairKey: `${ownerMacroStepKey}|cases|${ownerCaseIndexRaw}`,
            ownerMacroStepKey,
            ownerCaseIndex,
            slotFamily: 'cases',
            slotInOwner: 'cases/when',
            stepKey: whenToolStepKey,
        });
        upsertCasePair({
            pairByKey: branchPairByKey,
            pairKey: `${ownerMacroStepKey}|cases|${ownerCaseIndexRaw}`,
            ownerMacroStepKey,
            ownerCaseIndex,
            slotFamily: 'cases',
            slotInOwner: 'cases/what',
            stepKey: whatToolStepKey,
        });
    }

    for (const pair of branchPairByKey.values()) {
        appendCaseSeedFromPair(pair);
    }

    // Loop case pairs come from materialized macro-step control so their membership
    // are available before replay reaches the paired tool executions.
    for (const pair of collectLoopCasePairsFromTrace({ traceSnapshot, stepIndexByStepKey })) {
        appendCaseSeedFromPair(pair);
    }

    return seeds;
}

function deriveCaseSeeds(args: {
    cosmosData: CosmosDataModel;
    itemCollections: ItemCollection[];
    config: Config;
    traceSnapshot?: TraceSnapshotView;
}): CaseSeed[] {
    const caseConfig = resolveCasesConfig(args.config);
    if (!caseConfig.enabled) return [];
    const metrics = deriveToolStepLayoutMetrics(args);
    if (!metrics) return [];
    return deriveCaseMembership(args).flatMap((membership) => {
        const geometry = deriveCaseBandGeometry({
            whenRingRadius: metrics.ringRadii[membership.whenStepIndex],
            whatRingRadius: metrics.ringRadii[membership.whatStepIndex],
            whenTubeRadius: metrics.tubeRadii[membership.whenStepIndex] ?? resolveToolStepLayoutConfig(args.config).tubeRadius,
            whatTubeRadius: metrics.tubeRadii[membership.whatStepIndex] ?? resolveToolStepLayoutConfig(args.config).tubeRadius,
            gutter: caseConfig.gutter,
            minWidth: caseConfig.minWidth,
        });
        return geometry ? [{
            ...membership,
            sourceKind: membership.slotFamily === 'cases' ? 'branch' as const : 'loop' as const,
            ...geometry,
        }] : [];
    });
}

export function deriveToolStepItemCollectionDerivation(args: {
    itemCollectionKey: CollectionKey<'entity'>;
    cosmosData: CosmosDataModel;
    config: Config;
    traceSnapshot?: TraceSnapshotView;
}): EntityItemCollectionDerivation | undefined {
    const { config } = args;
    const traceSnapshot = args.traceSnapshot;
    if (!traceSnapshot) return undefined;

    const { orderedToolSteps } = traceSnapshot.position;
    if (orderedToolSteps.length === 0) return undefined;

    const itemDerivations = orderedToolSteps.map((toolStep) => toToolStepEntityItemDerivation({
        toolStep,
        config: resolveToolStepLayoutConfig(config),
    }));

    return buildToolStepItemCollectionDerivation({
        itemCollectionKey: args.itemCollectionKey,
        itemDerivations,
    });
}

export function deriveToolStepConstellationDerivation(args: {
    collectionKey: CollectionKey<'entity'>;
    constellationKey: ConstellationKey<'entity'>;
    cosmosData: CosmosDataModel;
    itemCollections: ItemCollection[];
    config: Config;
    traceSnapshot?: TraceSnapshotView;
}): EntityConstellationDerivation | undefined {
    const { cosmosData, itemCollections, config } = args;

    const toolStepCollection = itemCollections.find(
        (itemCollection): itemCollection is ItemCollection<'entity'> =>
            itemCollection.itemCollectionKind === 'entity'
            && itemCollection.itemCollectionKey === args.collectionKey
    );

    const firstToolStepItem = toolStepCollection?.items[0];
    if (!toolStepCollection || !firstToolStepItem) return undefined;

    const traceSnapshot = args.traceSnapshot;
    if (!traceSnapshot) return undefined;
    const metrics = deriveToolStepLayoutMetrics({
        cosmosData,
        itemCollections,
        config,
        traceSnapshot,
    });
    if (!metrics) return undefined;

    const ringRadii = metrics.ringRadii;

    const { startRadius, ringGap, tubeRadii } = metrics;
    const toolStepLayoutConfig = resolveToolStepLayoutConfig(config);

    return buildToolStepConstellationDerivation({
        constellationKey: args.constellationKey,
        toolStepCollection,
        firstToolStepItemKey: firstToolStepItem.itemKey,
        startRadius,
        ringGap,
        ringRadii,
        tubeRadii,
        toolStepLayoutConfig,
        caseMemberships: deriveCaseMembership(args),
    });
}

export function deriveCaseItemCollectionDerivations(
    args: CaseDerivationInput<CosmosDataModel, TraceSnapshotView> & { workflowKeyScope: WorkflowKeyScope }
): CaseCollectionsDerivation {
    const seeds = deriveCaseSeeds({
        cosmosData: args.cosmosData,
        itemCollections: args.itemCollections,
        config: requireConfig(args.config),
        ...(args.traceSnapshot ? { traceSnapshot: args.traceSnapshot } : {}),
    });
    if (seeds.length === 0) {
        return {
            itemCollectionDerivations: [],
            metadataByItemKey: new Map(),
        };
    }

    const caseConfig = resolveCasesConfig(args.config);

    const itemDerivationsByOwnerMacroStepKey = new Map<string, EntityItemDerivation[]>();
    const metadataByItemKey = new Map<EntityItem['itemKey'], CaseMetadata>();

    for (const seed of seeds) {
        const label = seed.slotFamily === 'cases'
            ? `Case ${seed.ownerCaseIndex}`
            : `Case ${seed.ownerCaseIndex + 1}`;

        const itemDerivation = toCaseItemDerivation({
            ownerMacroStepKey: seed.ownerMacroStepKey,
            ownerCaseIndex: seed.ownerCaseIndex,
            slotFamily: seed.slotFamily,
            innerRadius: seed.innerRadius,
            outerRadius: seed.outerRadius,
            whenBoundary: seed.whenBoundary,
            label,
            name: label,
            description: `MacroStep Case band (${seed.slotFamily})`,
            colorNearWhen: caseConfig.colorNearWhen,
            colorNearWhat: caseConfig.colorNearWhat,
            opacity: caseConfig.opacity,
            gradientCutoffCenter: caseConfig.gradientCutoffCenter,
            gradientCutoffWidth: caseConfig.gradientCutoffWidth,
        });

        const current = itemDerivationsByOwnerMacroStepKey.get(seed.ownerMacroStepKey) ?? [];
        current.push(itemDerivation);
        itemDerivationsByOwnerMacroStepKey.set(seed.ownerMacroStepKey, current);

        metadataByItemKey.set(itemDerivation.item.itemKey, {
            ownerMacroStepKey: seed.ownerMacroStepKey,
            ownerCaseIndex: seed.ownerCaseIndex,
            slotFamily: seed.slotFamily,
            whenToolStepKey: seed.whenToolStepKey,
            whatToolStepKey: seed.whatToolStepKey,
            whenStepIndex: seed.whenStepIndex,
            whatStepIndex: seed.whatStepIndex,
        });
    }

    const itemCollectionDerivations = buildCaseItemCollectionDerivations({
        itemDerivationsByOwnerMacroStepKey,
        scope: args.workflowKeyScope,
    });

    return {
        itemCollectionDerivations,
        metadataByItemKey,
    };
}

/**
 * Derives radial guide segments per ToolStep and ResourceType, spanning
 * continuously from the ResourceType ring to the outer-rim centerline.
 * Returns no guides when disabled or when either endpoint collection is empty.
 */
export function deriveResourceTypeRadialConnectorItemCollectionDerivations(args: {
    cosmosData: CosmosDataModel;
    itemCollections: ItemCollection[];
    config: Config;
    workflowKeyScope: WorkflowKeyScope;
    toolStepLayoutMetrics: ToolStepLayoutMetrics<TraceSnapshotView> | undefined;
}): ConnectorItemCollectionDerivation[] {
    const { cosmosData, itemCollections, config } = args;

    if (!isResourceTypeRadialConnectorsEnabled(config)) return [];
    const layoutMetrics = args.toolStepLayoutMetrics;
    if (!layoutMetrics) return [];

    const toolStepCollection = itemCollections.find(
        (c): c is ItemCollection<'entity'> =>
                c.itemCollectionKind === 'entity' && parseToolStepCollectionKey(c.itemCollectionKey) !== null
    );
    if (!toolStepCollection || toolStepCollection.items.length === 0) return [];
    if (layoutMetrics.ringRadii.length !== toolStepCollection.items.length
        || layoutMetrics.orderedToolSteps.length !== toolStepCollection.items.length) {
        throw new Error('ResourceType radial connectors require matching ToolStep items and ring metrics.');
    }

    // Derive ResourceType item keys by mirroring how toItem() produces them.
    // toItem() sets itemKey = `item:entity:${handle ?? resource.id}` where handle
    // comes from the projection. For ResourceType resources the handle is the
    // resourceTypeHandle (e.g. TYPE_Natural), so the item key is `item:entity:TYPE_Natural`.
    const resourceTypeItemKeys = Array.from(cosmosData.resourceTypeMap.keys())
        .map((handle) => `item:entity:${handle}` as EntityItem['itemKey']);

    if (resourceTypeItemKeys.length === 0) return [];

    const resourceTypeRingRadius = getResourceTypeRingRadius(config);
    const connectorItemDerivations = resourceTypeItemKeys.flatMap((resourceTypeItemKey) => (
        layoutMetrics.orderedToolSteps.flatMap((toolStep, stepIndex) => {
            const toolStepItem = toolStepCollection.items[stepIndex];
            const radius = layoutMetrics.ringRadii[stepIndex];
            if (!toolStepItem || radius === undefined) {
                throw new Error(`ResourceType radial connector is missing ToolStep ring ${stepIndex}.`);
            }
            const previousRadius = stepIndex === 0
                ? resourceTypeRingRadius
                : layoutMetrics.ringRadii[stepIndex - 1];
            const nextRadius = layoutMetrics.ringRadii[stepIndex + 1];
            if (previousRadius === undefined) {
                throw new Error(`ResourceType radial connector is missing inner boundary ${stepIndex}.`);
            }
            const innerBoundary = stepIndex === 0
                ? resourceTypeRingRadius
                : (previousRadius + radius) / 2;
            const outerBoundary = nextRadius === undefined
                ? radius + layoutMetrics.ringGap
                : (radius + nextRadius) / 2;
            const previousToolStepItem = stepIndex === 0
                ? undefined
                : toolStepCollection.items[stepIndex - 1];
            const nextToolStepItem = nextRadius === undefined
                ? undefined
                : toolStepCollection.items[stepIndex + 1];
            if (stepIndex > 0 && !previousToolStepItem) {
                throw new Error(`ResourceType radial connector is missing previous ToolStep item ${stepIndex}.`);
            }
            if (nextRadius !== undefined && !nextToolStepItem) {
                throw new Error(`ResourceType radial connector is missing next ToolStep item ${stepIndex}.`);
            }
            const fromInner = toResourceTypeRadialConnectorItemDerivation({
                resourceTypeItemKey,
                toolStepKey: toolStep.stepKey,
                direction: 'input',
                fromRingItemKey: previousToolStepItem?.itemKey ?? resourceTypeItemKey,
                toRingItemKey: toolStepItem.itemKey,
                fromRadialOffset: innerBoundary - previousRadius,
                config,
            });
            const toOuter = toResourceTypeRadialConnectorItemDerivation({
                resourceTypeItemKey,
                toolStepKey: toolStep.stepKey,
                direction: 'output',
                fromRingItemKey: toolStepItem.itemKey,
                toRingItemKey: nextToolStepItem?.itemKey ?? toolStepItem.itemKey,
                ...(nextRadius === undefined
                    ? { toRadialOffset: layoutMetrics.ringGap }
                    : { toRadialOffset: outerBoundary - nextRadius }),
                config,
            });
            if (resolveOperationPresentationMode(config) === 'panels') {
                for (const [direction, derivation] of [['input', fromInner], ['output', toOuter]] as const) {
                    const itemKeys = itemCollections.flatMap((collection) => (
                        collection.itemCollectionKind === 'entity' ? collection.items.filter((item) => {
                            const parsed = parseWorkflowStateEntryItemKey(item.itemKey);
                            return parsed?.stepKey === toolStep.stepKey && parsed.direction === direction
                                && `item:entity:${parsed.resourceTypeHandle}` === resourceTypeItemKey;
                        }).map((item) => item.itemKey) : []
                    ));
                    if (itemKeys.length > 0) {
                        derivation.parametricState = {
                            ...derivation.parametricState,
                            lineGaps: { itemKeys, clearance: RADIAL_PANEL_CLEARANCE },
                        };
                    }
                }
            }
            return [fromInner, toOuter];
        })
    ));

    return [buildResourceTypeRadialConnectorItemCollectionDerivation({
        scope: args.workflowKeyScope,
        itemDerivations: connectorItemDerivations,
    })];
}

function deriveMacroStepConnectorSeeds(
    args: MacroStepConnectorDerivationInput<CosmosDataModel, TraceSnapshotView>
): MacroStepConnectorSeed[] {
    const { traceSnapshot } = args;
    if (!traceSnapshot) return [];
    const stepIndexByStepKey = buildStepIndexByStepKey({
        orderedToolSteps: traceSnapshot.position.orderedToolSteps,
    });
    const rootLoopStepKeyByCloneLoopStepKey = deriveRootLoopStepKeyByCloneLoopStepKey({
        tickTracePoints: traceSnapshot.tickTracePoints,
        appliedTickCount: traceSnapshot.tickTracePoints.length,
    });

    const kindByMacroStepKey = new Map<string, MacroStepConnectorSeed['macroStepKind']>();
    const boundsByMacroStepKey = new Map<string, {
        minStepIndex: number;
        maxStepIndex: number;
        minStepKey: MutableToolStepKey;
        maxStepKey: MutableToolStepKey;
    }>();
    for (const membership of deriveCaseMembership({
        ...args,
        config: requireConfig(args.config),
    })) {
        const logicalMacroStepKey = membership.slotFamily === 'cases'
            ? membership.ownerMacroStepKey
            : normalizeLoopOwnerStepKey({
                ownerStepKey: membership.ownerMacroStepKey as MutableLoopStepKey,
                rootLoopStepKeyByCloneLoopStepKey,
            });
        if (!logicalMacroStepKey) continue;
        kindByMacroStepKey.set(logicalMacroStepKey, membership.slotFamily === 'cases' ? 'branch' : 'loop');
        const bounds = deriveToolStepBounds({
            aToolStepKey: membership.whenToolStepKey,
            bToolStepKey: membership.whatToolStepKey,
            stepIndexByStepKey,
        });
        if (!bounds) continue;

        upsertMacroStepBounds({
            boundsByMacroStepKey,
            macroStepKey: logicalMacroStepKey,
            nextBounds: bounds,
        });
    }

    const seeds: MacroStepConnectorSeed[] = [];
    for (const [logicalMacroStepKey, bounds] of boundsByMacroStepKey.entries()) {
        seeds.push({
            macroStepKind: kindByMacroStepKey.get(logicalMacroStepKey)!,
            logicalMacroStepKey,
            startToolStepKey: bounds.minStepKey,
            endToolStepKey: bounds.maxStepKey,
            startStepIndex: bounds.minStepIndex,
            endStepIndex: bounds.maxStepIndex,
        });
    }

    return seeds;
}

export function deriveMacroStepConnectorItemCollectionDerivations(
    args: MacroStepConnectorDerivationInput<CosmosDataModel, TraceSnapshotView> & { workflowKeyScope: WorkflowKeyScope }
): ConnectorItemCollectionDerivation[] {
    const macroStepConnectorConfig = resolveMacroStepConnectorsConfig(args.config);
    if (!isMacroStepConnectorsEnabled(args.config)) return [];

    const toolStepCollection = args.itemCollections.find(
        (itemCollection): itemCollection is ItemCollection<'entity'> =>
            itemCollection.itemCollectionKind === 'entity'
            && parseToolStepCollectionKey(itemCollection.itemCollectionKey) !== null
    );
    if (!toolStepCollection || toolStepCollection.items.length === 0) return [];

    const ownerSeeds = deriveMacroStepConnectorSeeds({
        cosmosData: args.cosmosData,
        itemCollections: args.itemCollections,
        config: args.config,
        ...(args.traceSnapshot ? { traceSnapshot: args.traceSnapshot } : {}),
    });
    const seeds = [
        ...ownerSeeds,
        ...(macroStepConnectorConfig.casePair?.enabled
            ? deriveCaseConnectorSeeds(deriveCaseMembership({
                ...args,
                config: requireConfig(args.config),
            }))
            : []),
    ];
    if (seeds.length === 0) return [];

    const resourceTypeInfo = deriveResourceTypeAngularInfo({
        itemCollections: args.itemCollections,
        config: args.config,
    });
    const laneAngles = resolveMacroStepHelperLaneAngles({ resourceTypeInfo, config: args.config });

    const itemDerivationsByCollectionKey = new Map<ItemCollection<'connector'>['itemCollectionKey'], ConnectorItemDerivation[]>();

    // Seed step indexes are pre-prune, so endpoints must be resolved by step key against current topology.
    const toolStepItemByStepKey = new Map<string, EntityItem>();
    for (const item of toolStepCollection.items) {
        const stepKey = parseToolStepKeyFromItemKey(item.itemKey);
        if (stepKey === null) continue;
        toolStepItemByStepKey.set(stepKey, item);
    }

    for (const seed of seeds) {
        const startToolStepItem = toolStepItemByStepKey.get(seed.startToolStepKey);
        const endToolStepItem = toolStepItemByStepKey.get(seed.endToolStepKey);
        if (!startToolStepItem || !endToolStepItem) continue;

        const itemDerivation = toMacroStepConnectorItemDerivation({
            logicalMacroStepKey: seed.logicalMacroStepKey,
            startToolStepKey: seed.startToolStepKey,
            endToolStepKey: seed.endToolStepKey,
            startToolStepItemKey: startToolStepItem.itemKey,
            endToolStepItemKey: endToolStepItem.itemKey,
            laneAngle: seed.macroStepKind === 'branch' ? laneAngles.branchAngle : laneAngles.loopAngle,
            macroStepKind: seed.macroStepKind,
            startStepIndex: seed.startStepIndex,
            endStepIndex: seed.endStepIndex,
            config: args.config,
            endRole: seed.endRole,
            workflowKeyScope: args.workflowKeyScope,
        });

        const collectionKey = buildMacroStepConnectorCollectionKey(args.workflowKeyScope);
        const current = itemDerivationsByCollectionKey.get(collectionKey) ?? [];
        current.push(itemDerivation);
        itemDerivationsByCollectionKey.set(collectionKey, current);
    }

    return buildMacroStepConnectorItemCollectionDerivations({
        itemDerivationsByCollectionKey,
    });
}

function deriveWorkflowStateEntryCollectionSeeds(
    args: WorkflowStateEntryDerivationInput<CosmosDataModel, TraceSnapshotView>
): SpokeCollectionSeed[] {
    const { cosmosData, itemCollections, config } = args;
    if (!resolveWorkflowStateEntriesConfig(config).enabled) return [];

    const toolStepCollection = itemCollections.find(
        (c): c is ItemCollection<'entity'> =>
            c.itemCollectionKind === 'entity' && parseToolStepCollectionKey(c.itemCollectionKey) !== null
    );
    if (!toolStepCollection || toolStepCollection.items.length === 0) return [];

    const resourceTypeInfo = deriveResourceTypeAngularInfo({ itemCollections, config });
    if (resourceTypeInfo.resourceTypeCount === 0) return [];
    const { angleByHandle: resourceTypeAngleByHandle } = resourceTypeInfo;

    const metrics = deriveToolStepLayoutMetrics({
        cosmosData,
        itemCollections,
        config: requireConfig(config),
        ...(args.traceSnapshot ? { traceSnapshot: args.traceSnapshot } : {}),
    });
    if (!metrics) return [];

    const { orderedToolSteps, startRadius, ringGap, ringRadii } = metrics;

    const seeds: SpokeCollectionSeed[] = [];

    orderedToolSteps.forEach((toolStep, stepIndex) => {
        const tool = cosmosData.toolMap.get(toolStep.toolHandle);
        if (!tool) return;

        const stepRadius = ringRadii[stepIndex] ?? (startRadius + stepIndex * ringGap);
        const stepKey = String(toolStep.stepKey);
        
        // Get the actual ToolStep item key from the collection
        const toolStepItem = toolStepCollection.items[stepIndex];
        if (!toolStepItem) return;

        const addSeedsForDirection = (direction: 'input' | 'output') => {
            const roleEntries = Object.entries(
                direction === 'input'
                    ? tool.roleSpec.inputRoleByName
                    : tool.roleSpec.outputRoleByName
            );

            const groupedByResourceType = new Map<ResourceTypeHandle, Array<[string, unknown]>>();
            for (const [roleName, role] of roleEntries) {
                const resourceTypeHandle = extractResourceTypeHandleFromRole(
                    role as Parameters<typeof extractResourceTypeHandleFromRole>[0]
                );
                if (!resourceTypeHandle) continue;

                const current = groupedByResourceType.get(resourceTypeHandle) ?? [];
                current.push([roleName, role]);
                groupedByResourceType.set(resourceTypeHandle, current);
            }

            for (const [resourceTypeHandle, groupedEntries] of groupedByResourceType.entries()) {
                const angle = resourceTypeAngleByHandle.get(resourceTypeHandle);
                if (angle == null) continue;
                const orderedEntries = direction === 'input'
                    ? [...groupedEntries].reverse()
                    : groupedEntries;

                const itemDerivations = orderedEntries.map(([roleName]) => toWorkflowStateEntryItemDerivation({
                    stepKey,
                    direction,
                    resourceTypeHandle,
                    roleName,
                    config,
                }));

                if (itemDerivations.length === 0) continue;

                const itemOffsets = buildItemOffsetsForSpoke({
                    itemCount: itemDerivations.length,
                    workflowStateEntryGap: metrics.workflowStateEntryGap,
                });

                seeds.push({
                    stepIndex,
                    stepKey,
                    stepItemKey: toolStepItem.itemKey,
                    direction,
                    resourceTypeHandle,
                    angle,
                    stepRadius,
                    radialOffset: metrics.workflowStateEntryGap,
                    itemDerivations,
                    itemOffsets,
                });
            }
        };

        addSeedsForDirection('output');
        addSeedsForDirection('input');
    });

    return seeds;
}

function enrichRunnerWorkflowStateEntryItemDerivation(args: {
    itemDerivation: EntityItemDerivation;
    config: Config;
    workflowState: MutableWorkflowState;
}): EntityItemDerivation {
    const parsed = parseWorkflowStateEntryItemKey(args.itemDerivation.item.itemKey);
    if (!parsed) return args.itemDerivation;

    const workflowStateEntriesDeclaration = args.config.domainPresentation.entities.workflowStateEntries;
    const resolution = resolveWorkflowStateEntryMaterializedEntry({
        direction: parsed.direction,
        stepKey: parsed.stepKey,
        roleName: parsed.roleName,
        workflowState: args.workflowState,
    });
    const baseOpacity = (() => {
        const configured = parsed.direction === 'input'
            ? workflowStateEntriesDeclaration.item.input.visualStyle?.opacity
            : workflowStateEntriesDeclaration.item.output.visualStyle?.opacity;
        return typeof configured === 'number' ? configured : 1;
    })();
    const previousState = args.itemDerivation.parametricState;

    return withMergedEntityItemParametricState(args.itemDerivation, {
        shape: previousState?.shape?.shapeKind === 'panel'
            ? {
                ...previousState.shape,
                appearanceMode: resolution.isFilled ? 'filled' : 'outlined',
            }
            : previousState?.shape,
        visualStyle: { opacity: baseOpacity },
        label: resolution.projectionText === null
            ? {}
            : {
                onItem: {
                    ...(previousState?.label?.onItem ?? {}),
                    ...(workflowStateEntriesDeclaration.item.label?.onItem ?? {}),
                    text: resolution.projectionText,
                },
            },
    });
}

export function deriveWorkflowStateEntryItemCollectionDerivations(
    args: WorkflowStateEntryDerivationInput<CosmosDataModel, TraceSnapshotView> & {
        workflowKeyScope: WorkflowKeyScope;
    }
): EntityItemCollectionDerivation[] {
    const seeds = deriveWorkflowStateEntryCollectionSeeds({
        cosmosData: args.cosmosData,
        itemCollections: args.itemCollections,
        config: args.config,
        ...(args.traceSnapshot ? { traceSnapshot: args.traceSnapshot } : {}),
    });

    const workflowState = args.traceSnapshot?.position.workflowState ?? {};
    const enrichedSeeds = seeds.map((seed) => ({
        ...seed,
        itemDerivations: seed.itemDerivations.map((itemDerivation) => enrichRunnerWorkflowStateEntryItemDerivation({
            itemDerivation,
            config: requireConfig(args.config),
            workflowState,
        })),
    }));

    return buildWorkflowStateEntryItemCollectionDerivations({ seeds: enrichedSeeds, scope: args.workflowKeyScope });
}

export function deriveWorkflowStateEntryConstellationDerivations(
    args: WorkflowStateEntryDerivationInput<CosmosDataModel, TraceSnapshotView> & { workflowKeyScope: WorkflowKeyScope }
): EntityConstellationDerivation[] {
    const { config } = args;
    const workflowStateEntriesConfig = resolveWorkflowStateEntriesConfig(config);
    if (!workflowStateEntriesConfig.enabled) return [];

    const seeds = deriveWorkflowStateEntryCollectionSeeds({
        cosmosData: args.cosmosData,
        itemCollections: args.itemCollections,
        config: args.config,
        ...(args.traceSnapshot ? { traceSnapshot: args.traceSnapshot } : {}),
    });
    if (seeds.length === 0) return [];

    return buildWorkflowStateEntryConstellationDerivations({
        seeds,
        workflowStateEntriesConfig,
        scope: args.workflowKeyScope,
    });
}

export function deriveWorkflowStateEntryConnectorItemCollectionDerivations(
    args: WorkflowStateEntryConnectorDerivationInput<CosmosDataModel, TraceSnapshotView> & { workflowKeyScope: WorkflowKeyScope }
): ConnectorItemCollectionDerivation[] {
    const { cosmosData, itemCollections, config } = args;
    if (!isWorkflowStateEntryConnectorsEnabled(config)) return [];

    const traceSnapshot = args.traceSnapshot;
    if (!traceSnapshot) return [];

    const { orderedToolSteps, workflowState } = traceSnapshot.position;
    if (orderedToolSteps.length === 0) return [];

    const workflowStateEntryItemKeySet = new Set<EntityItem['itemKey']>();
    for (const itemCollection of itemCollections) {
        if (itemCollection.itemCollectionKind !== 'entity') continue;
        if (!parseWorkflowStateEntryCollectionKey(itemCollection.itemCollectionKey)) continue;
        for (const item of itemCollection.items) {
            workflowStateEntryItemKeySet.add(item.itemKey);
        }
    }

    const stepIndexByStepKey = new Map<string, number>();
    const toolByStepKey = new Map<string, Tool>();
    orderedToolSteps.forEach((toolStep, stepIndex) => {
        stepIndexByStepKey.set(String(toolStep.stepKey), stepIndex);
        const tool = cosmosData.toolMap.get(toolStep.toolHandle);
        if (tool) {
            toolByStepKey.set(String(toolStep.stepKey), tool);
        }
    });

    const connectorItemDerivationsByCollectionKey = new Map<ItemCollection<'connector'>['itemCollectionKey'], ConnectorItemDerivation[]>();

    for (const targetToolStep of orderedToolSteps) {
        const targetStepKey = String(targetToolStep.stepKey);
        const targetStepIndex = stepIndexByStepKey.get(targetStepKey);
        if (targetStepIndex == null) continue;

        const targetTool = toolByStepKey.get(targetStepKey);
        if (!targetTool) continue;

        const targetInputRoleByName = targetTool.roleSpec.inputRoleByName as Record<string, unknown>;
        const targetInputEntries = workflowState[targetStepKey as keyof typeof workflowState];
        if (!targetInputEntries || typeof targetInputEntries !== 'object') continue;

        for (const [targetRoleName, entry] of Object.entries(targetInputEntries)) {
            const sourceAddress = tryReadInternalInputPotentialSourceAddress({ entry });
            if (!sourceAddress) continue;

            const sourceStepKey = String(sourceAddress.stepKey);
            const sourceRoleName = String(sourceAddress.roleName);
            const sourceStepIndex = stepIndexByStepKey.get(sourceStepKey);
            if (sourceStepIndex == null || sourceStepIndex >= targetStepIndex) continue;

            const targetRole = targetInputRoleByName[targetRoleName];
            if (!targetRole || typeof targetRole !== 'object') continue;
            const targetResourceTypeHandle = extractResourceTypeHandleFromRole(
                targetRole as Parameters<typeof extractResourceTypeHandleFromRole>[0]
            );
            if (!targetResourceTypeHandle) continue;

            const sourceTool = toolByStepKey.get(sourceStepKey);
            if (!sourceTool) continue;

            const sourceRole = resolveSourceRoleDirectionAndValue({
                tool: sourceTool,
                roleName: sourceRoleName,
            });
            if (!sourceRole) continue;

            const sourceDirection = sourceRole.direction;
            const sourceRoleValue = sourceRole.role;
            if (!sourceRoleValue || typeof sourceRoleValue !== 'object') continue;
            const sourceResourceTypeHandle = extractResourceTypeHandleFromRole(
                sourceRoleValue as Parameters<typeof extractResourceTypeHandleFromRole>[0]
            );
            if (!sourceResourceTypeHandle) continue;

            const endpointPair = deriveWorkflowStateEntryConnectorEndpointPair({
                ...args.workflowKeyScope,
                targetStepKey,
                targetRoleName,
                targetResourceTypeHandle,
                sourceStepKey,
                sourceRoleName,
                sourceDirection,
                sourceResourceTypeHandle,
                workflowStateEntryItemKeySet,
            });
            if (!endpointPair) continue;

            appendConnectorItemDerivationToCollectionGroup({
                groupedItemDerivationsByCollectionKey: connectorItemDerivationsByCollectionKey,
                collectionKey: endpointPair.collectionKey,
                connectorItemDerivation: toWorkflowStateEntryConnectorItemDerivation({
                    targetStepKey,
                    targetRoleName,
                    sourceStepKey,
                    sourceRoleName,
                    sourceDirection,
                    resourceTypeHandle: targetResourceTypeHandle,
                    targetItemKey: endpointPair.targetItemKey,
                    sourceItemKey: endpointPair.sourceItemKey,
                    config,
                }),
            });
        }
    }

    return buildWorkflowStateEntryConnectorItemCollectionDerivations({
        itemDerivationsByCollectionKey: connectorItemDerivationsByCollectionKey,
    });
}
